import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface PortaledMenu {
  readonly open: boolean;
  readonly wrapRef: React.RefObject<HTMLDivElement | null>;
  readonly menuRef: React.RefObject<HTMLDivElement | null>;
  readonly openMenu: () => void;
  readonly closeMenu: () => void;
}

/**
 * Plumbing for a menu portaled to `document.body` to escape the node card's
 * `overflow` clipping. `onOpen` fires on the closed → open transition only.
 */
export function usePortaledMenu(onOpen?: () => void): PortaledMenu {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => setOpen(false), []);
  const openMenu = useCallback(() => {
    if (open) return;
    onOpen?.();
    setOpen(true);
  }, [open, onOpen]);

  useEffect(() => {
    if (!open) return undefined;
    // An option click lands outside `wrapRef`, the menu being portaled away from
    // it, and would close the menu before the option's onClick fired.
    const close = (e: PointerEvent) => {
      const target = e.target instanceof Node ? e.target : null;
      if (!target) return;
      if (wrapRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, [open]);

  // Imperative because neither setState nor a JSX `style` attribute is allowed
  // here: the first thrashes on scroll, the second is an inline style.
  const positionMenu = useCallback(() => {
    const menu = menuRef.current;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!menu || !rect) return;
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style.left = `${rect.left}px`;
    menu.style.width = `${rect.width}px`;
  }, []);

  // Capture phase: the menu has to follow every overflow ancestor, not the
  // window alone.
  useLayoutEffect(() => {
    if (!open) return undefined;
    positionMenu();
    window.addEventListener('scroll', positionMenu, true);
    window.addEventListener('resize', positionMenu);
    return () => {
      window.removeEventListener('scroll', positionMenu, true);
      window.removeEventListener('resize', positionMenu);
    };
  }, [open, positionMenu]);

  // Wheel / pinch-zoom pan the canvas by CSS transform, emitting no pointer or
  // scroll event, so the fixed menu would hang detached from its node.
  useEffect(() => {
    if (!open) return undefined;
    const onWheel = (e: WheelEvent) => {
      const target = e.target instanceof Node ? e.target : null;
      if (target && menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('wheel', onWheel, true);
    return () => window.removeEventListener('wheel', onWheel, true);
  }, [open]);

  return { open, wrapRef, menuRef, openMenu, closeMenu };
}
